/**
 * Linguagem de IMPACTO de combate (Fase 9.1). Coerência leve e reutilizável para
 * NORMAL HIT / CRITICAL / KILL, reagindo a eventos e usando o ParticleSystem (pooled,
 * quality-gated). Sem geometry/material por hit. Mobile-first: normal hit é throttled
 * (a cada ~2 acertos) para não virar parede de partículas com ataques rápidos.
 */
import { events } from "../core/Events";
import { quality } from "../core/Quality";
import type { ParticleSystem } from "./Particles";
import { GameConfig } from "../config/GameConfig";

const HIT = 0xc9b6ff;
const CRIT = 0xffe08a;
const KILL = 0x9d7bff;

export class ImpactFX {
  private hitToggle = 0;

  constructor(private readonly particles: ParticleSystem) {
    events.on("damage:dealt", (d) => this.onDamage(d.crit, d.position.x, d.position.z, d.target));
    events.on("enemy:killed", (p) => this.onKill(p.position.x, p.position.z, p.elite === true, p.enemyId));
  }

  private get scale(): number {
    return quality.values.particleScale;
  }

  private onDamage(crit: boolean, x: number, z: number, target: "player" | "enemy"): void {
    if (this.scale <= 0) return;
    if (crit) {
      // CRITICAL: impacto maior + streak (sem exagero)
      this.particles.burst(x, 1.1, z, CRIT, { count: Math.round(7 * this.scale), speed: 7, dur: 0.4 });
      this.particles.burst(x, 1.3, z, CRIT, { count: Math.round(3 * this.scale), speed: 11, dur: 0.28 });
    } else if (target === "enemy") {
      // NORMAL HIT: faísca pequena, throttled (a cada 2 acertos)
      this.hitToggle ^= 1;
      if (this.hitToggle === 0) return;
      this.particles.burst(x, 1.0, z, HIT, { count: Math.round(3 * this.scale), speed: 4, dur: 0.25 });
    }
  }

  /** KILL: "Void Energy Pop" — release de energia. 10.7S: elite/campeão explode MUITO maior. */
  private onKill(x: number, z: number, elite: boolean, enemyId?: string): void {
    if (this.scale <= 0) return;
    // 10.9GS — a explosão leva a COR da criatura (cristal/corpo), não um roxo único pra tudo
    const def = enemyId ? (GameConfig.enemies as Record<string, { color?: number; crystalColor?: number }>)[enemyId] : undefined;
    const body = def?.crystalColor ?? def?.color ?? KILL;
    if (elite) {
      // explosão dourada grande + anel de fragmentos (morte de elite/campeão é evento)
      this.particles.burst(x, 1.1, z, CRIT, { count: Math.round(30 * this.scale), speed: 11, dur: 0.9 });
      this.particles.burst(x, 0.8, z, body, { count: Math.round(18 * this.scale), speed: 6, dur: 0.7 });
      this.particles.burst(x, 0.5, z, 0xffffff, { count: Math.round(8 * this.scale), speed: 3, dur: 0.5 });
    } else {
      this.particles.burst(x, 1.0, z, body, { count: Math.round(10 * this.scale), speed: 6, dur: 0.6 });
      this.particles.burst(x, 0.6, z, 0xd6c4ff, { count: Math.round(5 * this.scale), speed: 3, dur: 0.45 });
    }
  }
}
